import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface TermsOfServiceModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isVisible, onClose }) => {
  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10002,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem'
            }}
          >
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              padding: '1.5rem',
              maxWidth: '48rem',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827', margin: 0 }}>
                    Terms of Service
                  </h2>
                  <button
                    onClick={onClose}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.25rem'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Content */}
                <div style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#374151' }}>
                  <p style={{ marginBottom: '1rem' }}>
                    <strong>Last updated:</strong> {new Date().toLocaleDateString()}
                  </p>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      1. Acceptance of Terms
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      By accessing and using Triple Freezer, you accept and agree to be bound by the terms 
                      and provision of this agreement. If you do not agree to abide by the above, please 
                      do not use this service.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      2. Description of Service
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      Triple Freezer is a dice game that provides:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>Online dice game with multiplayer capabilities</li>
                      <li>Game statistics and performance tracking</li>
                      <li>User accounts and profiles</li>
                      <li>Social features and friend connections</li>
                      <li>Tournament and competitive play</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      3. User Accounts
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      When you create an account with us, you must provide accurate and complete information. 
                      You are responsible for safeguarding your account credentials and for all activities 
                      that occur under your account.
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>You must be at least 13 years old to create an account</li>
                      <li>You are responsible for maintaining account security</li>
                      <li>You may not share your account with others</li>
                      <li>We reserve the right to terminate accounts that violate our terms</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      4. Acceptable Use
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      You agree not to use the service to:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li>Violate any applicable laws or regulations</li>
                      <li>Harass, abuse, or harm other users</li>
                      <li>Share inappropriate or offensive content</li>
                      <li>Attempt to gain unauthorized access to our systems</li>
                      <li>Use automated tools to manipulate game outcomes</li>
                      <li>Interfere with the proper functioning of the service</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      5. Intellectual Property
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      The service and its original content, features, and functionality are owned by 
                      Triple Freezer and are protected by international copyright, trademark, patent, 
                      trade secret, and other intellectual property laws.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      6. Privacy and Data Protection
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      Your privacy is important to us. Please review our Privacy Policy, which also 
                      governs your use of the service, to understand our practices.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      7. Termination
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We may terminate or suspend your account and bar access to the service immediately, 
                      without prior notice or liability, under our sole discretion, for any reason 
                      whatsoever, including without limitation if you breach the Terms.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      8. Limitation of Liability
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      In no event shall Triple Freezer, nor its directors, employees, partners, agents, 
                      suppliers, or affiliates, be liable for any indirect, incidental, special, 
                      consequential, or punitive damages, including without limitation, loss of profits, 
                      data, use, goodwill, or other intangible losses.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      9. Changes to Terms
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      We reserve the right, at our sole discretion, to modify or replace these Terms 
                      at any time. If a revision is material, we will provide at least 30 days notice 
                      prior to any new terms taking effect.
                    </p>
                  </section>

                  <section style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                      10. Contact Information
                    </h3>
                    <p style={{ marginBottom: '0.5rem' }}>
                      If you have any questions about these Terms of Service, please contact us:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
                      <li><strong>Email:</strong> legal@triple-freezer.com</li>
                      <li><strong>Website:</strong> triple-freezer.com</li>
                    </ul>
                  </section>
                </div>

                {/* Footer */}
                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', textAlign: 'center' }}>
                  <button
                    onClick={onClose}
                    style={{
                      padding: '0.5rem 1.5rem',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      borderRadius: '0.5rem',
                      border: 'none',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default TermsOfServiceModal; 